/**
 * Credential detayı — the reference's `data-screen="credential"`.
 *
 * Ported against the rendered DOM of `renderCredDetail` (`m08b-admin2.js:188`): one page, no tab
 * bar, a `.split` whose wide column stacks «Public sayfa ve bağlantılar», «Kayıt» and «Denetim
 * kaydı», and whose narrow column carries the rendered artwork.
 *
 * The action row is the part that changes with the record, and it changes in four ways — awaiting
 * acceptance, awaiting an administrator's publication, live, and revoked or superseded. Each one
 * offers exactly the commands that state permits: a record awaiting acceptance can have its claim
 * link re-minted but not its holder corrected, a revoked one can only be looked at and reissued,
 * and a reissued one cannot even be reissued again because its successor already exists.
 *
 * Two things the screen refuses to fake, both from §6.3/§13 of the reference: a claim link whose
 * raw token is no longer in the session is shown as UNAVAILABLE with the copy button disabled
 * rather than as an ellipsis someone could paste, and the second timeline tab is labelled as
 * usage measurement, not as audit — the append-only ledger is the legal record and page views are
 * not part of it.
 */
import {
  Fragment,
  type ReactElement,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { newIdempotencyKey } from '../../api/client';
import { useAdoptCredentials } from '../data/adopt';
import { useV07Commands } from '../data/commands-context';
import { useV07Queries } from '../data/queries-context';
import { useV07Role } from '../data/session';
import { useDemoData, useSettings } from '../data/store';
import type {
  DemoCredential,
  DemoEvent,
  DemoFieldDefinition,
  DemoRelease,
  DemoTemplate,
} from '../data/types';
import { useRead } from '../data/use-read';
import { RESEND_TARGET_TEXT, useResendTarget } from '../data/use-resend-target';
import { sendAct } from '../lib/bulk-send';
import {
  ACTOR,
  buildOgImage,
  contactEmailOutcome,
  copyText,
  correctableFields,
  correctionDiff,
  downloadCredentialPng,
  eventMeta,
  LI_TR,
  type LinkedInField,
  linkedInAllText,
  linkedInFields,
  printCredential,
  recordTelemetry,
  subscribeTelemetry,
  telemetryFor,
  telemetryVersion,
} from '../lib/credential-detail';
import {
  claimTokenView,
  claimUrlStatus,
  hasActionReason,
  type IssueProblem,
  mintPublicCode,
  rawClaimToken,
  validateIssue,
} from '../lib/credential-register';
import {
  addToProfileLinkForCredential,
  credentialIssuingOrganization,
} from '../lib/linkedin-issuing-organization';
import {
  credentialViewState,
  credPermissions,
  credTitle,
  currentRelease,
  dataTypeRec,
  deliveryLabel,
  displayName,
  EVENT_TR,
  editableFields,
  fieldLabel,
  fmtDate,
  fmtDateTime,
  imageUrl,
  nextExpiry,
  ogUrl,
  printUrl,
  publicUrl,
  publishedTemplates,
  pubPolicyRec,
  STATUS_ICON,
  STATUS_PILL,
  STATUS_TR,
  stateForPolicy,
  trUpper,
  VISIBILITY_TR,
} from '../lib/reference';
import { canvasH, canvasW, credentialArtwork, credTerms, typeRec } from '../lib/render';
import { uploadLabel } from '../lib/upload-name';
import { Modal, ModalHead } from '../ui/Modal';
import { PublicLink } from '../ui/PublicLink';
import { useToast } from '../ui/toast';
import { CredentialHistoryCard } from './CredentialHistoryCard';
import { FirstSendPanel } from './FirstSendPanel';

const typeLabel = (r: DemoRelease): string =>
  r.credentialType === 'custom' && r.customTypeLabel ? r.customTypeLabel : typeRec(r.credentialType).tr;

/** `canRender` — `m01c-validate.js:10`. Only these two visibilities may reach a render layer. */
const canRender = (v: string): boolean => v === 'public_render' || v === 'public_metadata_and_render';

const claimField = (c: DemoCredential, key: string): string => String(c.claims[key] ?? '');

/**
 * A copy of the fields a correction is judged against, taken when the dialog opens.
 *
 * By VALUE, not by reference: the store is written in place, so holding the same object would hold
 * whatever it becomes. Only the parts the dialog prefills from are copied — the claims and the two
 * dates — which is exactly the set `correctionDiff` reads.
 */
const snapshotOf = (c: DemoCredential): DemoCredential => ({ ...c, claims: { ...c.claims } });

/**
 * Everything a correction has to remember about the moment the dialog opened: the values the form
 * was filled from, and the identity of the revision they came from.
 */
const openCorrection = (c: DemoCredential): { credential: DemoCredential; baseRevisionId: string } => ({
  credential: snapshotOf(c),
  baseRevisionId: String((c.revisions.at(-1) as { id?: string } | undefined)?.id ?? ''),
});

/** The claim token's own metadata block; the seed types `claimToken` as a loose record. */
function tokenMeta(c: DemoCredential): Record<string, unknown> {
  return c.claimToken ?? {};
}

/**
 * What the acceptance-link area says when there is no link to show, by the SERVER's own reason.
 *
 * Four different facts, four different sentences. «The raw link is not in this session» is not
 * evidence that the link expired, that it was used, or that the e-mail never arrived — it is a
 * property of where the raw token lives (only in the memory of the session that minted it), and
 * saying anything stronger would invite a send nobody needed.
 */
export const CLAIM_NOTICE: Record<string, string> = {
  raw_token_not_in_session:
    'Alıcı onayı bekleniyor. Önceki onay bağlantısı güvenlik nedeniyle burada tekrar ' +
    'görüntülenemiyor. Gerekirse yeni bir onay e-postası gönderebilirsiniz.',
  token_consumed: 'Bu onay bağlantısı kullanıldı; alıcının işlemi kayda geçti.',
  token_revoked: 'Önceki onay bağlantısı iptal edildi. Gerekirse yeni bir onay e-postası gönderebilirsiniz.',
  token_expired:
    'Onay bağlantısının süresi doldu. Alıcının onaylayabilmesi için yeni bir onay e-postası gönderin.',
  no_claim_token: 'Bu kayıt onay bağlantısı taşımıyor.',
};

export const claimNotice = (reason: string | null): string =>
  CLAIM_NOTICE[reason ?? ''] ?? 'Alıcı onayı bekleniyor; onay bağlantısının durumu okunamadı.';

/**
 * A technical value the record may simply not carry.
 *
 * The detail read returns a claim token's lifecycle fields and deliberately returns none of its
 * secret-class ones, so `Number(tok.entropyBits)` printed «NaN bit» and `String(tok.encoding)`
 * printed nothing at all. An unknown value is now said to be unknown; it is never filled with a
 * zero, a guess or a dash that reads like a measurement.
 */
const knownText = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const knownCount = (v: unknown): string | null =>
  typeof v === 'number' && Number.isFinite(v) ? String(v) : null;

function IconExt(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M14 4h6v6" />
      <path d="M20 4l-9 9" />
      <path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
    </svg>
  );
}

function IconPrint(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M6 9V3h12v6" />
      <rect x="3" y="9" width="18" height="9" rx="2" />
      <path d="M6 14h12v7H6z" />
    </svg>
  );
}

function IconCopy(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </svg>
  );
}

// ------------------------------------------------------------------------------------- dialogs

function RevokeDialog({
  credential,
  onClose,
}: {
  credential: DemoCredential;
  onClose: (reason: string | null) => void;
}): ReactElement {
  const [reason, setReason] = useState('');
  return (
    <Modal label="İptal" onClose={() => onClose(null)}>
      <ModalHead title="Kayıt iptal edilsin mi?" onClose={() => onClose(null)} />
      <div className="card-pad">
        <p>
          <b>{displayName(credential)}</b>
          {` · ${credTitle(credential, 'tr')} · `}
          <span className="mono">{credential.publicCode}</span>
        </p>
        <div className="field">
          <label htmlFor="rvReason">Neden (özel; kayıt defterine yazılır, public sayfada gösterilmez)</label>
          <textarea
            className="textarea"
            id="rvReason"
            placeholder="ör. Yanlış alıcıya düzenlendi"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <div className="info-box">
          {'Public sayfa silinmez; aynı adreste '}
          <b>«İptal edildi»</b>
          {
            ' durumu ve iptal tarihi gösterilir, görsele filigran basılır. İptal geri alınamaz — yanlış alıcı için yeni kayıt (reissue) çıkarılır, eski kaydın geçmişi korunur.'
          }
        </div>
      </div>
      <div className="card-foot">
        <button className="btn btn-ghost" data-close="1" type="button" onClick={() => onClose(null)}>
          Vazgeç
        </button>
        <button className="btn btn-dark" id="rvGo" type="button" onClick={() => onClose(reason)}>
          İptal et
        </button>
      </div>
    </Modal>
  );
}

function ResendDialog({
  credential,
  available,
  onClose,
}: {
  credential: DemoCredential;
  available: boolean;
  onClose: (reason: string | null) => void;
}): ReactElement {
  const pending = credential.state === 'pending_acceptance';
  const tok = claimTokenView(credential);
  const [reason, setReason] = useState(
    pending ? 'Alıcı onay bağlantısına ulaşamadı' : 'Alıcı e-postayı bulamadı',
  );
  const [error, setError] = useState(false);
  const target = useResendTarget(credential);
  /* One press is one send. A deliberate LATER send is a new window; a second click is not. */
  const submitted = useRef(false);

  return (
    <Modal label="Yeniden gönder" onClose={() => onClose(null)} wide>
      <ModalHead
        title={
          pending ? 'Yeni onay bağlantısı üret ve yeniden gönder' : 'Bilgilendirme e-postasını yeniden gönder'
        }
        onClose={() => onClose(null)}
      />
      <div className="card-pad stack" style={{ gap: 12 }}>
        {pending ? (
          <div className="warn-box">
            <b>Bu işlem YENİ bir onay bağlantısı üretir.</b>
            {` Alıcıya daha önce gönderilmiş bağlantı geçersiz olur; yalnız yeni bağlantı çalışır.${
              available
                ? ''
                : ' Mevcut ham bağlantı bu oturumda bulunmadığı için zaten yalnız yeni bağlantı üretilebilir.'
            }`}
          </div>
        ) : (
          <div className="info-box">
            Bu kayıt yayınlanmış. Alıcıya sertifikanın herkese açık sayfasını içeren bir bilgilendirme
            e-postası gönderilir; yeniden onay istenmez ve mevcut bağlantılar etkilenmez.
          </div>
        )}
        <dl className="dl">
          <dt>Kayıt</dt>
          <dd>
            {`${displayName(credential)} · `}
            <span className="mono">{credential.publicCode}</span>
          </dd>
          <dt>Hedef e-posta</dt>
          <dd id="rsTarget" data-target={target.status}>
            {target.status === 'ready' ? (
              <b>{target.email}</b>
            ) : (
              <span className="muted">{RESEND_TARGET_TEXT[target.status]}</span>
            )}
          </dd>
          <dt>İşlemi yapan</dt>
          <dd>{ACTOR}</dd>
          {pending && tok?.hash ? (
            <>
              <dt>Mevcut token</dt>
              <dd className="small mono">{`${tok.hash.slice(0, 12)}… · rotasyon ${tok.rotationCount}`}</dd>
            </>
          ) : null}
        </dl>
        <div className="field">
          <label htmlFor="rsReason">Gerekçe (zorunlu)</label>
          <input
            className="input"
            id="rsReason"
            required
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setError(false);
            }}
          />
        </div>
        <div className="warn-box" hidden={!error}>
          Gerekçe zorunludur ve en az 3 karakter olmalıdır.
        </div>
      </div>
      <div className="card-foot">
        <button className="btn btn-ghost" data-close="1" type="button" onClick={() => onClose(null)}>
          Vazgeç
        </button>
        <button
          className="btn btn-primary"
          id="rsGo"
          type="button"
          /* Never offered before the address is known: a send with no target is a send to nowhere. */
          disabled={target.status !== 'ready'}
          onClick={() => {
            if (submitted.current || target.status !== 'ready') return;
            if (!hasActionReason(reason)) {
              setError(true);
              return;
            }
            submitted.current = true;
            onClose(reason);
          }}
        >
          {pending ? 'Yeni bağlantı üret ve gönder' : 'Yeniden gönder'}
        </button>
      </div>
    </Modal>
  );
}

export interface CorrectionResult {
  claims: Record<string, string>;
  issueDate: string;
  expiresAt: string;
  reason: string;
}

function CorrectDialog({
  credential,
  refusal,
  busy,
  onClose,
  onReissue,
}: {
  credential: DemoCredential;
  /**
   * The BACKEND's refusal, kept beside the form rather than instead of it.
   *
   * The dialog used to close before the command ran, so a refusal — the stale-base 409 above all —
   * threw away everything that had been typed and left the operator with a toast and an empty desk.
   * A 409 means somebody else corrected this record while this form was open, which is exactly the
   * moment the form is worth keeping.
   */
  refusal: string | null;
  /** True while the command is in flight, so one click cannot become two corrections. */
  busy: boolean;
  onClose: (result: CorrectionResult | null) => void;
  onReissue: () => void;
}): ReactElement {
  const fields = correctableFields(credential);
  const [claims, setClaims] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.key, claimField(credential, f.key)])),
  );
  const [issueDate, setIssueDate] = useState(credential.issueDate);
  const [expiresAt, setExpiresAt] = useState(credential.expiresAt ?? '');
  const [reason, setReason] = useState('');
  const [refused, setRefused] = useState<string | null>(null);

  return (
    <Modal label="Düzelt" onClose={() => onClose(null)}>
      <ModalHead title="Yazım hatasını düzelt" onClose={() => onClose(null)} />
      <div className="card-pad">
        <div className="warn-box">
          <b>Kural:</b>
          {' Bu işlem yalnız '}
          <b>kimlik dışı</b>
          {" alanları aynı public kodla düzeltir ve audit'e yazar. Sahip alanı ("}
          <span className="mono">{credential.release.holderField}</span>
          {') burada değiştirilemez; yanlış alıcı için «İptal et ve yeniden düzenle» kullanın.'}
        </div>
        <div className="field">
          <label htmlFor="cxHolder">Sahip (değiştirilemez)</label>
          <input className="input" id="cxHolder" value={displayName(credential)} disabled />
        </div>
        {fields.length === 0 ? (
          <p className="muted">Bu şablonda düzeltilebilir kimlik dışı alan yok.</p>
        ) : (
          fields.map((f) => (
            <div className="field" key={f.key}>
              <label htmlFor={`cx_${f.key}`}>
                {`${fieldLabel(f, 'tr')}${canRender(f.visibility) ? ' (görselde basılan)' : ''}`}
              </label>
              <input
                className="input"
                id={`cx_${f.key}`}
                data-cx={f.key}
                value={claims[f.key] ?? ''}
                onChange={(e) => setClaims((prev) => ({ ...prev, [f.key]: e.target.value }))}
              />
            </div>
          ))
        )}
        <div className="field-row">
          <div className="field">
            <label htmlFor="cxIssue">Veriliş tarihi</label>
            <input
              className="input"
              id="cxIssue"
              type="date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="cxExp">Geçerlilik</label>
            <input
              className="input"
              id="cxExp"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>
        </div>
        <div className="field">
          <label htmlFor="cxReason">Düzeltme nedeni (audit kaydına yazılır)</label>
          <input
            className="input"
            id="cxReason"
            placeholder="ör. Soyadı yazım hatası"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <div className="info-box">
          {`Public kod ve URL aynı kalır; görsel yeniden render edilir, revision ${credential.revision} → ${credential.revision + 1} olur ve `}
          <code>corrected</code>
          {' olayı eski/yeni değerlerle kaydedilir.'}
        </div>
        <div id="cxResult">
          {refusal === null ? null : (
            <div className="warn-box" role="alert" id="cxRefusal">
              <b>Düzeltilemedi.</b>
              {` ${refusal}`}
            </div>
          )}
          {refused === null ? null : (
            <>
              <div className="warn-box">
                <b>Reddedildi.</b>
                {` ${refused}`}
              </div>
              <button
                className="btn btn-dark btn-sm"
                type="button"
                id="cxToReissue"
                style={{ marginTop: 8 }}
                onClick={onReissue}
              >
                İptal et ve yeniden düzenle
              </button>
            </>
          )}
        </div>
      </div>
      <div className="card-foot">
        <button
          className="btn btn-ghost"
          data-close="1"
          type="button"
          disabled={busy}
          onClick={() => onClose(null)}
        >
          Vazgeç
        </button>
        <button
          className="btn btn-primary"
          id="cxGo"
          type="button"
          disabled={busy}
          onClick={() => {
            /* Rule 5 is enforced by the command, not by the form: the dialog never offers an
               identity field, and if one arrived anyway the answer is the reissue flow. */
            const diff = correctionDiff(credential, {
              claims,
              issueDate,
              expiresAt: expiresAt || null,
            });
            if (diff.identity.length > 0) {
              setRefused(
                'Sahip/alıcı kimliği düzeltme ile değiştirilemez. «İptal et ve yeniden düzenle» akışını kullanın.',
              );
              return;
            }
            onClose({ claims, issueDate, expiresAt, reason });
          }}
        >
          {busy ? 'Kaydediliyor…' : 'Kaydet ve yeniden render et'}
        </button>
      </div>
    </Modal>
  );
}

function EmailFixDialog({
  credential,
  onClose,
}: {
  credential: DemoCredential;
  onClose: (result: { email: string; resend: boolean } | null) => void;
}): ReactElement {
  const [email, setEmail] = useState(credential.privateContactEmail ?? '');
  const [resend, setResend] = useState(true);
  return (
    <Modal label="E-posta düzelt" onClose={() => onClose(null)}>
      <ModalHead title="İletişim e-postasını düzelt" onClose={() => onClose(null)} />
      <div className="card-pad">
        <div className="field">
          <label htmlFor="emNew">Yeni e-posta</label>
          <input
            className="input"
            id="emNew"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <label className="check">
          <input
            type="checkbox"
            id="emResend"
            checked={resend}
            onChange={(e) => setResend(e.target.checked)}
          />
          {` Düzeltmeden sonra ${credential.publishedAt ? 'bilgilendirme' : 'onay'} e-postasını yeniden gönder`}
        </label>
        <div className="help">
          {
            "E-posta özel iletişim bilgisidir; subject kimliğini değiştirmez, public metadata ve görsele hiçbir zaman girmez. Değişiklik audit'e yazılır."
          }
        </div>
      </div>
      <div className="card-foot">
        <button className="btn btn-ghost" data-close="1" type="button" onClick={() => onClose(null)}>
          Vazgeç
        </button>
        <button
          className="btn btn-primary"
          id="emGo"
          type="button"
          onClick={() => onClose({ email, resend })}
        >
          Kaydet
        </button>
      </div>
    </Modal>
  );
}

export interface ReissueResult {
  templateId: string;
  claims: Record<string, string>;
  email: string;
  issueDate: string;
  expiresAt: string;
  lang: 'tr' | 'en';
  sendEmail: boolean;
  reason: string;
}

/**
 * «İptal et ve yeniden düzenle» — `openReissueModal` (`m08b-admin2.js:150`), which is the single
 * issuance dialog prefilled from the old record. It is one command, not two: the warning says so
 * because a UI that revoked first and then issued would leave a revoked record with no successor
 * whenever the second half failed (rule 8).
 */
function ReissueDialog({
  credential,
  templates,
  onClose,
}: {
  credential: DemoCredential;
  templates: DemoTemplate[];
  onClose: (result: ReissueResult | null) => void;
}): ReactElement {
  const [templateId, setTemplateId] = useState(credential.templateId);
  const [claims, setClaims] = useState<Record<string, string>>(() =>
    Object.fromEntries(Object.entries(credential.claims).map(([k, v]) => [k, String(v ?? '')])),
  );
  const [email, setEmail] = useState(credential.privateContactEmail ?? '');
  const [issueDate, setIssueDate] = useState(credential.issueDate);
  const [expiresAt, setExpiresAt] = useState(credential.expiresAt ?? '');
  const [lang, setLang] = useState<'tr' | 'en'>('tr');
  const [sendEmail, setSendEmail] = useState(true);
  const [reason, setReason] = useState('Yeniden düzenlendi');
  const [problems, setProblems] = useState<IssueProblem[]>([]);

  const template = templates.find((t) => t.id === templateId);
  const release = currentRelease(template);
  const fields: DemoFieldDefinition[] = release ? editableFields(release.fieldDefinitions) : [];
  const policy = release ? pubPolicyRec(release.publicationPolicy) : null;

  /* `syncType`: the expiry follows the chosen release's own validity policy, not a free date. */
  useEffect(() => {
    if (release === null) return;
    setExpiresAt(nextExpiry(release.validityPolicy, issueDate) ?? '');
  }, [release, issueDate]);

  return (
    <Modal label="Yeniden düzenle" onClose={() => onClose(null)}>
      <ModalHead title="İptal et ve yeniden düzenle" onClose={() => onClose(null)} />
      <div className="card-pad">
        <div className="warn-box">
          {'Bu işlem tek bir işlemdir: eski kayıt '}
          <span className="mono">{credential.publicCode}</span>
          {
            ' iptal edilir ve yeni kayıt yeni kod ile oluşturulur. İki kayıt karşılıklı bağlanır; herhangi bir adım başarısız olursa hiçbir değişiklik kalmaz.'
          }
        </div>
        <div className="field">
          <label htmlFor="siTpl">{"Şablon (yalnız yayınlanmış release'ler)"}</label>
          <select
            className="select"
            id="siTpl"
            value={templateId}
            onChange={(e) => {
              setTemplateId(e.target.value);
              setProblems([]);
            }}
          >
            {templates.map((t) => {
              const r = currentRelease(t);
              return (
                <option key={t.id} value={t.id}>
                  {r ? `${t.name} · v${r.releaseNumber} · ${typeLabel(r)}` : t.name}
                </option>
              );
            })}
          </select>
        </div>
        <div id="siPolicy">
          {policy === null ? null : (
            <div className="info-box small">
              <b>Yayın politikası:</b>
              {` ${policy.tr} — ${policy.help}`}
            </div>
          )}
        </div>
        <div id="siFields">
          {fields.length === 0 ? (
            <p className="muted small">{"Bu release'te dinamik alan yok."}</p>
          ) : (
            fields.map((f) => (
              <div className="field" key={f.key}>
                <label htmlFor={`si_${f.key}`}>
                  {`${fieldLabel(f, lang)}${f.required ? ' *' : ''} `}
                  <span className="small muted">
                    {`${dataTypeRec(f.dataType).tr} · ${VISIBILITY_TR[f.visibility] ?? ''}`}
                  </span>
                </label>
                <input
                  className="input"
                  id={`si_${f.key}`}
                  data-si={f.key}
                  value={claims[f.key] ?? ''}
                  placeholder={f.sample}
                  onChange={(e) => setClaims((prev) => ({ ...prev, [f.key]: e.target.value }))}
                />
              </div>
            ))
          )}
        </div>
        <div className="field">
          <label htmlFor="siEmail">İletişim e-postası (özel)</label>
          <input
            className="input"
            id="siEmail"
            type="email"
            placeholder="ad@sirket.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="siIssue">Veriliş tarihi</label>
            <input
              className="input"
              id="siIssue"
              type="date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="siExp">Geçerlilik</label>
            <input
              className="input"
              id="siExp"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="siLang">Dil</label>
            <select
              className="select"
              id="siLang"
              value={lang}
              onChange={(e) => setLang(e.target.value === 'en' ? 'en' : 'tr')}
            >
              <option value="tr">Türkçe</option>
              <option value="en">English</option>
            </select>
          </div>
          <div className="field">
            <label className="check" style={{ marginTop: 26 }}>
              <input
                type="checkbox"
                id="siSend"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
              E-posta gönder (simülasyon)
            </label>
          </div>
        </div>
        <div className="field">
          <label htmlFor="siReason">İptal nedeni (özel)</label>
          <input className="input" id="siReason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <div className="help">
          {
            "Alanlar ve doğrulama kuralları seçilen release'in alan tanımlarından üretilir — tek kayıt, CSV ve XLSX aynı doğrulama motorunu kullanır."
          }
        </div>
        <div id="siResult">
          {problems.length === 0 ? null : (
            <div className="warn-box">
              <b>Oluşturulamadı.</b>
              <ul style={{ margin: '6px 0 0 18px' }}>
                {problems.map((p) => (
                  <li key={`${p.key ?? ''}-${p.message}`}>{p.message}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      <div className="card-foot">
        <button className="btn btn-ghost" data-close="1" type="button" onClick={() => onClose(null)}>
          Vazgeç
        </button>
        <button
          className="btn btn-primary"
          id="siGo"
          type="button"
          onClick={() => {
            if (release === null) return;
            const found = validateIssue(release, claims, email, lang);
            setProblems(found);
            if (found.length > 0) return;
            onClose({ templateId, claims, email, issueDate, expiresAt, lang, sendEmail, reason });
          }}
        >
          İptal et ve yeni kaydı oluştur
        </button>
      </div>
    </Modal>
  );
}

/**
 * The LinkedIn helper — `openLinkedInHelper` (`m05-linkedin.js:53`), opened from this screen with
 * `lang: 'tr'`.
 *
 * The link this dialog opens CARRIES the certificate (PO 7.9 §1): name, issuing organisation,
 * issue and expiry month/year, credential id and verification URL travel as query parameters, so
 * the LinkedIn form arrives filled in. The per-field copy list stays beside it as the fallback for
 * anything the member still wants to adjust, and for the case where a record cannot describe
 * itself completely.
 *
 * Rules 14 and 15 still hold and are not weakened by this: there is no extension, no userscript,
 * no DOM automation and no API write. The member reviews LinkedIn's own form and presses Save
 * themselves, so nothing here is a one-click automatic add.
 */
function LinkedInDialog({
  credential,
  fields,
  issuerName,
  pageUrl,
  unreadableIssuer,
  addToProfileUrl,
  onClose,
  onCopy,
}: {
  credential: DemoCredential;
  fields: LinkedInField[];
  /**
   * The resolved «Veren kuruluş» — the template's own organisation when it named one, the record's
   * issuer otherwise. Already decided by `credentialIssuingOrganization`, so the copy helper, the
   * preview and the Add-to-Profile link cannot name three different organisations.
   */
  issuerName: string;
  /** That organisation's own LinkedIn Page, or null when it has none to open. */
  pageUrl: string | null;
  /** The record's own stored organisation cannot be read: name nobody, offer no shortcuts. */
  unreadableIssuer: boolean;
  /** The filled-in Add-to-Profile link for this record, or null when it cannot be built. */
  addToProfileUrl: string | null;
  onClose: () => void;
  onCopy: (text: string, message: string) => void;
}): ReactElement {
  const [copied, setCopied] = useState<ReadonlySet<number>>(new Set());
  const [current, setCurrent] = useState(0);
  const expired = credentialViewState(credential) === 'expired';
  const issued = fmtDate(credential.issueDate, 'tr', 'month');

  const copyField = (i: number): void => {
    const f = fields[i];
    if (f === undefined) return;
    onCopy(f.value, `${f.label}: ${LI_TR.copied}`);
    const next = new Set(copied);
    next.add(i);
    setCopied(next);
    const after = fields.findIndex((_, j) => j > i && !next.has(j));
    setCurrent(after >= 0 ? after : fields.findIndex((_, j) => !next.has(j)));
  };

  return (
    <Modal label={LI_TR.title} onClose={onClose} drawer wide>
      <div className="card-head">
        <div>
          <span className="eyebrow">{LI_TR.eyebrow}</span>
          <h2 style={{ marginTop: 6 }}>{LI_TR.title}</h2>
        </div>
        <button className="icon-btn" type="button" data-close="1" aria-label={LI_TR.close} onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="card-pad li-helper" lang="tr">
        {expired ? (
          <div className="warn-box">
            <b>{`⚠ ${LI_TR.expiredWarn}`}</b>
          </div>
        ) : null}
        <p className="muted" style={{ fontSize: '13.5px' }}>
          {LI_TR.intro}
        </p>
        <div className="warn-box" style={{ display: 'grid', gap: 8 }}>
          <span>
            <b>{`${LI_TR.fields.org ?? ''}:`}</b>
            {` ${unreadableIssuer ? LI_TR.issuerUnreadable : LI_TR.issuerWarn(issuerName)}`}
          </span>
          {unreadableIssuer ? null : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                className="btn btn-secondary btn-sm"
                type="button"
                id="liCopyIssuer"
                onClick={() => onCopy(issuerName, LI_TR.copied)}
              >
                <IconCopy />
                {LI_TR.copyIssuer}
              </button>
              {pageUrl ? (
                <a
                  className="btn btn-secondary btn-sm"
                  href={pageUrl}
                  target="_blank"
                  rel="noopener"
                  id="liOpenPage"
                >
                  {LI_TR.openPage}
                </a>
              ) : null}
            </div>
          )}
        </div>
        <div className="li-progress">
          <b id="liProgressText">{LI_TR.progress(copied.size, fields.length)}</b>
          <div className="progress">
            <i id="liProgressBar" style={{ width: `${Math.round((copied.size / fields.length) * 100)}%` }} />
          </div>
        </div>
        <div className="li-fields" id="liFields">
          {fields.map((f, i) => (
            <div
              className={`li-field ${copied.has(i) ? 'is-copied' : i === current ? 'is-current' : ''}`}
              data-idx={i}
              key={f.key}
            >
              <div className="field">
                <label htmlFor={`liF${i}`}>{f.label}</label>
                <input className="input input-sm" id={`liF${i}`} readOnly value={f.value} />
              </div>
              <button
                className="btn btn-secondary btn-sm li-copy"
                type="button"
                data-copy={i}
                title={LI_TR.copyNext}
                aria-label={`${f.label} · ${LI_TR.copyNext}`}
                onClick={() => copyField(i)}
              >
                <IconCopy />
                <span>{copied.has(i) ? LI_TR.copied : LI_TR.copy}</span>
              </button>
            </div>
          ))}
        </div>
        {credential.expiresAt ? null : (
          <div className="info-box" style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span>∞</span>
            <span>{LI_TR.noExpiry}</span>
          </div>
        )}
        <div className="li-preview-wrap">
          <div className="label" style={{ marginBottom: 8 }}>
            {LI_TR.previewTitle}
          </div>
          <div className="li-preview" lang="tr">
            <div className="li-logo" title={LI_TR.mockPreview}>
              <span className="mark" />
              <span className="li-logo-note">temsilî</span>
            </div>
            <div className="li-body">
              <b>{credTitle(credential)}</b>
              <span>{issuerName}</span>
              <span className="muted">
                {`${LI_TR.previewIssued} ${issued} · ${
                  credential.expiresAt
                    ? `${LI_TR.previewExp} ${fmtDate(credential.expiresAt, 'tr', 'month')}`
                    : LI_TR.previewNoExp
                }`}
              </span>
              <span className="muted">{`Credential ID ${credential.publicCode}`}</span>
              <PublicLink
                to={`/c/${credential.publicCode}`}
                target="_blank"
                rel="noopener"
                className="li-show"
              >
                {`${LI_TR.previewShow} ↗`}
              </PublicLink>
            </div>
          </div>
          <p className="help" style={{ marginTop: 8 }}>
            {`${LI_TR.mockPreview} ${LI_TR.previewNote}`}
          </p>
        </div>
      </div>
      <div className="card-foot li-actions">
        <button
          className="btn btn-secondary"
          type="button"
          id="liAll"
          onClick={() => {
            onCopy(linkedInAllText(credential, issuerName), LI_TR.allCopied);
            setCopied(new Set(fields.map((_, i) => i)));
            setCurrent(-1);
          }}
        >
          <IconCopy />
          {LI_TR.all}
        </button>
        <a
          className="btn li-btn"
          href={addToProfileUrl ?? undefined}
          aria-disabled={addToProfileUrl ? undefined : true}
          target="_blank"
          rel="noopener"
          id="liOpen"
          onClick={() => recordTelemetry(credential.id, 'linkedin_helper_opened', { reopen: true })}
        >
          <span className="in">in</span>
          {LI_TR.open}
        </a>
        <button
          className="btn btn-ghost"
          type="button"
          id="liRestart"
          onClick={() => {
            setCopied(new Set());
            setCurrent(0);
          }}
        >
          {LI_TR.restart}
        </button>
        <button className="btn btn-primary" type="button" id="liSavedBtn" onClick={onClose}>
          {LI_TR.saved}
        </button>
        <button className="btn btn-ghost" type="button" data-close="1" onClick={onClose}>
          {LI_TR.close}
        </button>
      </div>
    </Modal>
  );
}

function OgPreviewDialog({
  credential,
  onClose,
}: {
  credential: DemoCredential;
  onClose: () => void;
}): ReactElement {
  const [src, setSrc] = useState('');
  useEffect(() => {
    let live = true;
    void buildOgImage(credential, trUpper(credTerms(credential, 'tr').verified))
      .then((data) => {
        if (live) setSrc(data);
      })
      .catch(() => {
        /* the placard the render plan draws when it cannot verify an asset is answer enough */
      });
    return () => {
      live = false;
    };
  }, [credential]);

  return (
    <Modal label="Paylaşım önizlemesi" onClose={onClose} wide>
      <ModalHead title="Paylaşım önizlemesi" onClose={onClose} />
      <div className="card-pad">
        <div className="og-preview">
          <img id="ogImg2" alt="OG önizleme" width="1200" height="630" src={src || undefined} />
          <div className="og-meta">
            <b>{`${credTitle(credential)} · ${displayName(credential)}`}</b>
            <span>{publicUrl(credential).replace(/^https?:\/\//, '')}</span>
          </div>
        </div>
        <p className="help">
          {'Asset sözleşmesi: '}
          <code>/c/:code</code>
          {' · '}
          <code>/c/:code/image.png</code>
          {' · '}
          <code>/c/:code/print</code>
          {' · '}
          <code>/c/:code/og.png</code>
          {
            " (1200×630). Üretimde bu metadata JavaScript sonradan çalıştırılarak değil, public route'un ilk HTML cevabında server-side üretilmelidir."
          }
        </p>
      </div>
    </Modal>
  );
}

// -------------------------------------------------------------------------------------- screen

type Dialog =
  | 'revoke'
  | 'resend'
  | 'firstSend'
  | 'firstSendResult'
  | 'correct'
  | 'email'
  | 'reissue'
  | 'linkedin'
  | 'og'
  | null;

export function CredentialDetailScreen(): ReactElement {
  const params = useParams();
  const code = params.code ?? params.publicCode ?? params.id ?? '';
  const db = useDemoData();
  const queries = useV07Queries();
  const commands = useV07Commands();
  const role = useV07Role();
  const settings = useSettings();
  const toast = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'audit' | 'telemetry'>('audit');
  const [dialog, setDialog] = useState<Dialog>(null);

  /* The usage stream lives outside the demo database on purpose (see `credential-detail.ts`), so
     the tab counter has to subscribe to it rather than to the provider. */
  useSyncExternalStore(subscribeTelemetry, telemetryVersion, telemetryVersion);

  /*
   * The record is fetched BY THE CODE IN THE ADDRESS, not found in a list that was loaded earlier.
   *
   * That distinction was the defect. The screen looked the code up in the store, the store held one
   * page of the register, and so the two-hundred-and-third credential — a link in an e-mail, a code
   * someone read off a certificate — opened as «Kayıt bulunamadı» about a record that existed and
   * that the session was allowed to read. It is read through the authorized internal route; the
   * anonymous public projection is a narrower, different view and is not management data.
   */
  const lookup = useRead(`credential:${code}`, () => queries.credentialByCode(code));
  const fetched = lookup.value?.kind === 'found' ? lookup.value.credential : null;
  /*
   * A stable array, because `[fetched]` written inline is a new array on every render and the
   * adoption effect would then run on every render — write, re-render, write (see `adopt.ts`).
   */
  const fetchedRows = useMemo(() => (fetched === null ? null : [fetched]), [fetched]);
  useAdoptCredentials(fetchedRows);

  /*
   * What only a single-record read answers: the claims, the revisions, the append-only ledger and
   * the delivery attempts. The register's list projection deliberately carries none of them — two
   * hundred rows have no business fetching two hundred ledgers — so the screen that opens ONE
   * record asks for them here. The demo answers immediately; its seed is already whole.
   */

  /*
   * The record the correction dialog was OPENED on.
   *
   * «Did the operator change anything?» is a question about what they were shown, and the dialog
   * prefills its fields once, at mount. Answering it against the record as it stands at SUBMIT time
   * compares two different things: the store can be patched while the dialog is open — a late
   * hydration, an adoption, a refresh a command triggered — and every field then reads as changed
   * against a form nobody touched. The correction that produced was «request shape is invalid»,
   * which is both wrong and unactionable. So the diff is taken against the snapshot the operator
   * actually saw — and so is the REVISION the correction is written against, because the lock that
   * catches a record somebody else moved on has to name the version this form was filled from. It is
   * captured here rather than read at send time for the same reason the values are.
   */
  const [correctBase, setCorrectBase] = useState<{
    credential: DemoCredential;
    baseRevisionId: string;
  } | null>(null);
  /** The backend's own refusal of a correction, shown IN the dialog so the form is not lost. */
  const [correctRefusal, setCorrectRefusal] = useState<string | null>(null);
  const [correctBusy, setCorrectBusy] = useState(false);
  /**
   * The name of the send the resend window is about to make, minted when the window OPENS.
   *
   * Every press inside one opening is the same send, so the server can answer a retry with the
   * message it already queued; opening the window again is a new intent and mints a new name, which
   * is exactly the deliberate second send an operator is entitled to make.
   */
  const [resendKey, setResendKey] = useState(() => newIdempotencyKey());
  useEffect(() => {
    if (!code || fetched === null) return;
    void commands.hydrateCredential({ code });
  }, [code, commands, fetched]);

  /*
   * The record on screen is the one the CURRENT read found. The store is consulted only for a richer
   * copy of THAT record, never as a substitute for a verdict that says something else.
   *
   * Reading the store first was two bugs in one line. Moving from record A to record B kept A on
   * screen — with A's revoke and correct buttons — until B answered, because A was still in the
   * store; and a re-read of A that came back `forbidden` showed A anyway, so a refusal was invisible.
   * A cache may make an answer faster. It may not decide what the answer is.
   */
  const stored = fetched === null ? undefined : db.credentials.find((x) => x.id === fetched.id);
  /*
   * …and it is consulted for a RICHER copy, so it only wins when it actually is one.
   *
   * «The store first» was written for the hydrated record, which carries the claims and the ledger
   * the read does not. But the store can also be holding the REGISTER's projection of this record,
   * whose `claims` is `{}` by design, and that one is poorer. Preferring it blindly put an empty
   * correction form on screen for the moment between arriving and hydrating — and a correction is
   * judged against what the form was filled from.
   */
  const richer =
    stored !== undefined &&
    Object.keys(stored.claims ?? {}).length >= Object.keys(fetched?.claims ?? {}).length;
  const c = fetched === null ? undefined : richer ? stored : fetched;
  /*
   * «İLK E-POSTAYI GÖNDER» — is there a first e-mail to send for this record?
   *
   * A certificate created with its e-mail switched off is complete and nobody has been told about
   * it. The server decides whether that is this record's situation: the same rule that decides it
   * for a hundred records decides it for one, so the screen cannot offer an action the server would
   * refuse. Eligible means no e-mail exists for this record AT ALL — not «no claim attempt», which
   * is true of every immediately published certificate whether it was notified or not.
   *
   * Above the early return below, because that is where hooks have to be; the token carries the id
   * so a different record asks its own question. The frozen review surface answers «no» for every
   * record it holds — each has been sent its e-mail — so the button is not drawn there.
   */
  /*
   * THE SENDS THIS RECORD HAS ALREADY BEEN PART OF.
   *
   * Read whatever the record's eligibility is, because this is the way BACK to a result: once the
   * first e-mail has gone the record is no longer eligible and the button above disappears, and
   * without this the only route to «what happened» would be starting another send. The request is
   * a row on the server, so a closed window, a refreshed page and a new session all find it.
   */
  const firstSendHistory = useRead(`first-send-history:${c?.id ?? ''}`, async () => {
    if (c === undefined)
      return [] as { id: string; kind: string; createdAt: string; counts?: Record<string, number> }[];
    if (!(role === null || role === 'owner' || role === 'admin' || role === 'operator')) return [];
    const r = await commands.listFirstSendRequests({ credentialId: c.id, limit: 1 });
    return r.ok && r.requests ? r.requests : [];
  });
  const firstSendEligible = useRead(`first-send:${c?.id ?? ''}`, async () => {
    if (c === undefined) return false;
    /* A viewer may not send, so it is not asked either: an ordinary page view should not produce a
       refused command and an authorization failure in the server's log. */
    if (!(role === null || role === 'owner' || role === 'admin' || role === 'operator')) return false;
    const r = await commands.previewFirstSend({ scope: { credentialIds: [c.id] } });
    return r.ok && (r.preview?.eligible ?? 0) > 0;
  });

  if (c === undefined) {
    /*
     * Four outcomes, four sentences. «There is no such record», «you may not read this one» and «the
     * server did not answer» are three different facts with three different next steps, and the
     * fourth is that the answer has not arrived yet. Printing «Kayıt bulunamadı» for all of them told
     * an administrator that a record had been deleted when the server was merely unreachable.
     */
    /*
     * A verdict or a failure, and they are not the same kind of thing.
     *
     * `forbidden` and `not_found` are the server's ANSWERS about this code. A `ReadFailure` is the read
     * not happening — and it is the only one of the four that is worth a retry button, because the
     * other three would give the same answer again.
     */
    const outcome = lookup.value?.kind ?? null;
    const pending = lookup.status === 'loading';
    const failure = lookup.status === 'error' ? lookup.error : null;
    const heading = pending
      ? 'Kayıt yükleniyor…'
      : failure?.kind === 'session'
        ? 'Oturum sona erdi'
        : outcome === 'forbidden' || failure?.kind === 'forbidden'
          ? 'Bu kaydı görme yetkiniz yok'
          : failure !== null
            ? 'Kayıt okunamadı'
            : 'Kayıt bulunamadı';
    const note = pending
      ? `${code} okunuyor.`
      : failure?.kind === 'session'
        ? 'Yeniden giriş yapıp tekrar deneyin.'
        : outcome === 'forbidden' || failure?.kind === 'forbidden'
          ? `${code} bu rolün okuyabileceği kayıtlar arasında değil.`
          : failure !== null
            ? (failure.message ?? 'Sunucuya ulaşılamadı.')
            : `${code} koduyla eşleşen bir kayıt yok.`;
    const retryable = !pending && failure !== null && failure.kind !== 'session';
    return (
      <section data-screen="credential">
        <div id="credDetail">
          <div className="page-head">
            <div>
              <Link
                className="small"
                to="/admin/credentials"
                style={{ color: 'var(--text-3)', textDecoration: 'none' }}
              >
                {"← Credential'lar"}
              </Link>
              <h1>{heading}</h1>
              <p className="lead">{note}</p>
              {retryable ? (
                <button
                  className="btn btn-secondary btn-sm"
                  type="button"
                  id="credDetailRetry"
                  onClick={lookup.retry}
                >
                  Yeniden dene
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    );
  }

  /*
   * A refresh that failed leaves the previous answer on screen, and it has to say so. «This is what
   * the server last told us» and «this is what the server says» are different claims, and after a
   * command the difference is the whole question. A refusal is NOT this case: `credentialByCode`
   * answers `forbidden` rather than failing, so the record leaves the screen and the refusal shows.
   */
  const staleNotice =
    lookup.stale && lookup.status === 'error' ? (
      <div className="warn-box" role="status" id="credStale">
        <b>Bu kayıt yenilenemedi; aşağıdakiler en son okunan bilgiler.</b>{' '}
        {lookup.error?.kind === 'session' ? null : (
          <button
            className="btn btn-secondary btn-sm"
            type="button"
            id="credStaleRetry"
            onClick={lookup.retry}
          >
            Yeniden dene
          </button>
        )}
      </div>
    ) : null;

  const rel = c.release;
  const t = db.templates.find((x) => x.id === c.templateId);
  const s = credentialViewState(c);
  const batch = c.batchId ? (db.batches.find((x) => x.id === c.batchId) ?? null) : null;
  /*
   * WHICH GROUPS THIS RECORD IS IN (migration 028).
   *
   * The server's answer when there is one — a hydrated record carries it — and the demo's own store
   * otherwise, because the demo surface has no server and its groups live there. Not derived from
   * the batch: the upload a record came from is provenance and is a different question.
   */
  const groupsOf = (cred: DemoCredential): { id: string; name: string }[] =>
    cred.groups ??
    (db.credentialGroups ?? [])
      .filter((g) => g.credentialIds.includes(cred.id))
      .map((g) => ({ id: g.id, name: g.name }));
  const isOrg = (c.subjectSnapshot as { subjectType?: string }).subjectType === 'organization';
  const snapshotHash = String((c.subjectSnapshot as { snapshotHash?: string }).snapshotHash ?? '');
  const perm = credPermissions(c);
  const canDl = perm.allowDownload && s !== 'revoked' && s !== 'superseded';
  const pending = s === 'pending_acceptance';
  const rawTok = rawClaimToken(db, c);
  const claimUrl = claimUrlStatus(db, c);
  const tok = tokenMeta(c);
  const sup = c.supersedesCode
    ? (db.credentials.find((x) => x.publicCode === c.supersedesCode) ?? null)
    : null;
  const supBy = c.supersededByCode
    ? (db.credentials.find((x) => x.publicCode === c.supersededByCode) ?? null)
    : null;
  const [deliveryText, deliveryClass] = deliveryLabel(c.deliveryStatus);
  const usage = telemetryFor(c.id);
  const published = publishedTemplates(db);
  /* One resolution for the whole screen: the template's own LinkedIn organisation when this
     record's release froze one, the issuer otherwise — name, Page and verified id together. */
  const linkedInOrg = credentialIssuingOrganization(c.release, settings);

  const copy = (text: string, message?: string): void => copyText(text, toast, message);

  // ------------------------------------------------------------------------ command callbacks

  const finishRevoke = async (reason: string | null): Promise<void> => {
    setDialog(null);
    if (reason === null) return;
    const r = await commands.revokeCredential({ code: c.publicCode, reason });
    toast(
      r.ok
        ? 'Kayıt iptal edildi — public sayfa «İptal edildi» gösteriyor'
        : (r.message ?? 'İptal edilemedi: bu durumdan geçiş yok'),
    );
  };

  const finishResend = async (reason: string | null): Promise<void> => {
    setDialog(null);
    if (reason === null) return;
    const rotated = c.state === 'pending_acceptance';
    const rotation = (claimTokenView(c)?.rotationCount ?? 0) + 1;
    const r = await commands.resendClaimEmail({
      code: c.publicCode,
      reason,
      idempotencyKey: resendKey,
    });
    if (!r.ok) {
      toast(r.message ?? 'Gönderilemedi');
      return;
    }
    toast(
      rotated
        ? `Yeni onay bağlantısı üretildi ve gönderim kuyruğuna alındı · rotasyon ${rotation}`
        : 'Bilgilendirme e-postası gönderim kuyruğuna alındı',
    );
  };

  const closeCorrection = (): void => {
    setDialog(null);
    setCorrectBase(null);
    setCorrectRefusal(null);
    setCorrectBusy(false);
  };

  const finishCorrect = async (result: CorrectionResult | null): Promise<void> => {
    const opened = correctBase;
    if (result === null) {
      closeCorrection();
      return;
    }
    setCorrectRefusal(null);
    const input = { claims: result.claims, issueDate: result.issueDate, expiresAt: result.expiresAt || null };
    /*
     * A correction has to know what it is correcting. An issue date the dialog never had is not a
     * correction anybody asked for, so it is refused HERE rather than sent: what came back from the
     * backend for such a body was «request shape is invalid», which tells the operator nothing and
     * hides the real problem, and a body that reached it would have been a correction blanking
     * fields nobody edited. The control that opens this dialog waits for the record for the same
     * reason; this is the second door on the same room.
     */
    if (!result.issueDate) {
      closeCorrection();
      toast('Kaydın alanları okunmadan düzeltme gönderilemez', 'warn');
      return;
    }
    /* The identity guard and the no-op guard are the reference's own, reported without a round trip;
       the backend enforces both again, which is why this can be a check rather than a decision. */
    /* Against what the operator was shown, not against what the store holds a moment later. */
    const base = opened?.credential ?? c;
    if (Object.keys(correctionDiff(base, input).changes).length === 0) {
      closeCorrection();
      toast('Değişiklik yok');
      return;
    }
    /*
     * Only what CHANGED. Sending the whole claim set made every untouched field part of the
     * correction, which turned a date fix into a mixed change — and a mixed change is reason
     * `other`, which the backend requires an internal note for. The diff is the reference's own.
     */
    const changed = correctionDiff(base, input).changes;
    setCorrectBusy(true);
    const r = await commands.correctCredential({
      code: c.publicCode,
      claims: Object.fromEntries(
        Object.keys(changed)
          .filter((k) => k in result.claims)
          .map((k) => [k, result.claims[k] ?? '']),
      ),
      issueDate: result.issueDate,
      expiresAt: result.expiresAt || null,
      reasonCode: 'correction',
      reason: result.reason,
      /* The revision the form was filled from — so a record somebody else moved on comes back 409. */
      baseRevisionId: opened?.baseRevisionId ?? '',
    });
    setCorrectBusy(false);
    if (!r.ok) {
      /*
       * Refused — and the window STAYS OPEN with everything the operator typed still in it. The
       * refusal that matters most here is the stale-base 409: somebody else corrected this record
       * while the dialog was open, and the answer to that is «look at what changed and decide», not
       * «type it all again». Closing first made the 409 a dead end.
       */
      setCorrectRefusal(r.message ?? 'Düzeltilemedi');
      return;
    }
    closeCorrection();
    toast(`Kayıt düzeltildi ve yeniden render edildi (revision ${c.revision + 1})`);
  };

  const finishEmailFix = async (result: { email: string; resend: boolean } | null): Promise<void> => {
    setDialog(null);
    if (result === null) return;
    const outcome = contactEmailOutcome(c, result.email);
    if (outcome !== 'ok') {
      toast(outcome === 'no_change' ? 'Değişiklik yok' : 'Geçerli bir e-posta girin');
      return;
    }
    const r = await commands.updateRecipientEmail({
      code: c.publicCode,
      email: result.email,
      reason: 'Alıcı yeni adresini bildirdi',
      resend: result.resend,
    });
    toast(r.ok ? 'İletişim e-postası düzeltildi' : (r.message ?? 'Düzeltilemedi'));
  };

  const finishReissue = async (result: ReissueResult | null): Promise<void> => {
    setDialog(null);
    if (result === null) return;
    const release = currentRelease(published.find((x) => x.id === result.templateId));
    if (release === null) return;
    /*
     * Rule 8: revoking the old record and issuing the new one is ONE act. The screen never does it
     * in two steps — a failure between them would leave the recipient with neither record.
     */
    const r = await commands.reissueCredential({
      supersedesCode: c.publicCode,
      reason: result.reason,
      release,
      claims: result.claims,
      email: result.email,
      issueDate: result.issueDate,
      expiresAt: result.expiresAt || null,
      lang: result.lang,
      sendEmail: result.sendEmail,
      publicCode: mintPublicCode(db),
    });
    if (!r.ok || !r.publicCode) {
      toast(r.message ?? 'Yeniden üretilemedi');
      return;
    }
    toast(`Kayıt oluşturuldu · durum: ${STATUS_TR[stateForPolicy(release.publicationPolicy)]}`);
    void navigate(`/admin/credentials/${r.publicCode}`);
  };

  const openLinkedIn = (): void => {
    /* Same click, same tab budget as the reference: the LinkedIn form opens first, the helper
       second, so the browser treats the new tab as user-initiated rather than as a pop-up. */
    recordTelemetry(c.id, 'linkedin_helper_opened', { popupBlocked: false });
    setDialog('linkedin');
  };

  const openOg = (): void => {
    recordTelemetry(c.id, 'og_preview_opened', null);
    setDialog('og');
  };

  // ------------------------------------------------------------------------------ action row

  /*
   * What the surface OFFERS, from the authorization matrix. Not authorization — the backend
   * refuses every one of these for a role that may not have it, and a typed URL still gets its
   * 403 — but §11 is explicit that a control belongs to the roles that may use it, and a button
   * that exists only to be refused is the decorative button the brief forbids.
   *
   * `role === null` is the visual acceptance demo, which has no session: it sees the reference's
   * own screen, which is the state a capture must be compared in.
   */
  const mayLifecycle = role === null || role === 'owner' || role === 'admin';
  const mayResend = role === null || role === 'owner' || role === 'admin' || role === 'operator';

  const publicPageLink = (label: string): ReactElement => (
    <PublicLink className="btn btn-secondary" to={`/c/${c.publicCode}`} target="_blank" rel="noopener">
      {`${label} `}
      <IconExt />
    </PublicLink>
  );

  /*
   * One control, drawn only when there is something to open. It opens the result, never a send.
   *
   * It is labelled from the REQUEST's own act, because since migration 029 the newest request over
   * this record may be a re-send or a reminder — and a button that said «ilk e-posta» about a
   * reminder would be naming the wrong act for the result it opens.
   */
  const lastFirstSend = firstSendHistory.value?.[0] ?? null;
  /*
   * A SEND THAT HAS BEEN ACCEPTED AND NOT YET WORKED IS NOT A SEND THAT HAS NOT BEEN ASKED FOR.
   *
   * The record's eligibility is computed from the record — its state, its address and its delivery
   * rows — and a target nobody has claimed yet produces no delivery row, so a record whose first
   * send is sitting in an accepted request still reads as «eligible» indefinitely. In a deployment
   * with no worker that is for ever, and the screen offered the primary «İlk e-postayı gönder»
   * the whole time. An operator who had already pressed it, saw nothing arrive, and came back to
   * that button would reasonably press it again — and a reopened window is a new intent with a new
   * idempotency key, so that press writes a SECOND request with a second waiting target. No second
   * e-mail can come of it (the work-time baselines settle the loser as `newer_delivery_exists` or
   * `claim_token_changed`), but the register then holds more requests than sends anybody made,
   * which is exactly what makes «was the press even saved» hard to answer afterwards.
   *
   * So while the newest request still has something waiting, the way back to it leads, and the
   * send button is not drawn. The eligibility read, the server's verdict and the result window are
   * untouched: this is about which control this screen offers, not about what may be sent.
   */
  const waitingSend = (lastFirstSend?.counts?.pending ?? 0) > 0;
  const firstSendButton =
    mayResend && firstSendEligible.value === true && !waitingSend ? (
      /* `claim.resend` — A·A·A·D, the cell that already means «send this record's e-mail». */
      <button
        className="btn btn-primary"
        type="button"
        id="cdFirstSend"
        onClick={() => setDialog('firstSend')}
      >
        İlk e-postayı gönder
      </button>
    ) : null;
  const firstSendResultButton =
    mayResend && lastFirstSend !== null ? (
      <button
        /* Primary while it is the only way forward, so the screen leads with the open operation. */
        className={waitingSend ? 'btn btn-primary' : 'btn btn-secondary'}
        type="button"
        id="cdFirstSendResult"
        onClick={() => setDialog('firstSendResult')}
      >
        {sendAct(lastFirstSend.kind).resultTitle}
      </button>
    ) : null;

  let actions: ReactNode;
  if (pending)
    actions = (
      <>
        {firstSendButton}
        {firstSendResultButton}
        {mayResend ? (
          /* `claim.resend` — A·A·A·D */
          <button
            className="btn btn-secondary"
            type="button"
            id="cdResend"
            onClick={() => {
              setResendKey(newIdempotencyKey());
              setDialog('resend');
            }}
          >
            Yeni onay bağlantısı üret ve yeniden gönder
          </button>
        ) : null}
        {rawTok ? (
          <>
            <button
              className="btn btn-secondary"
              type="button"
              id="cdCopyClaim"
              onClick={() => copy(claimUrl.url ?? '', 'Claim bağlantısı kopyalandı')}
            >
              Claim bağlantısını kopyala
            </button>
            <PublicLink className="btn btn-secondary" to={`/claim/${rawTok}`} target="_blank" rel="noopener">
              {'Claim sayfasını aç '}
              <IconExt />
            </PublicLink>
          </>
        ) : /*
         * NOTHING here when there is no usable link.
         *
         * A disabled «Claim bağlantısı kullanılamıyor» button was a second control for a job the
         * «Yeni onay bağlantısı üret ve yeniden gönder» button above already does, and it read as
         * a broken feature rather than as an ordinary state. What the state is, and what to do
         * about it, is said once — in the acceptance-link area of the links card.
         */
        null}
        {mayLifecycle ? (
          /* the recipient's contact address — the correction cell, A·A·D·D (matrix D8) */
          <button
            className="btn btn-secondary"
            type="button"
            id="cdEmailFix"
            onClick={() => setDialog('email')}
          >
            E-posta adresini düzelt
          </button>
        ) : null}
        {mayLifecycle ? (
          /* `credential.correct_non_identity_metadata` — A·A*·D·D */
          <button
            className="btn btn-secondary"
            type="button"
            id="cdCorrect"
            onClick={() => {
              setCorrectBase(openCorrection(c));
              setDialog('correct');
            }}
          >
            Düzelt
          </button>
        ) : null}
        <button className="btn btn-dark" type="button" id="cdRevoke" onClick={() => setDialog('revoke')}>
          İptal et
        </button>
      </>
    );
  else if (s === 'pending_admin_publish')
    actions = (
      <>
        {mayLifecycle ? (
          /* `credential.admin_publish` — A·A·D·D, and only from `pending_admin_publish` */
          <button
            className="btn btn-primary"
            type="button"
            id="cdAdminPublish"
            onClick={() => {
              void (async () => {
                const r = await commands.adminPublishCredential({
                  code: c.publicCode,
                  reason: 'Yönetici onayıyla yayınlandı',
                });
                toast(
                  r.ok
                    ? 'Kayıt yayınlandı — public sayfa artık herkese açık'
                    : (r.message ?? 'Yayınlanamadı: geçersiz durum geçişi'),
                );
              })();
            }}
          >
            Yayınla (yönetici onayı)
          </button>
        ) : null}
        {mayLifecycle ? (
          /* `credential.correct_non_identity_metadata` — A·A*·D·D */
          <button
            className="btn btn-secondary"
            type="button"
            id="cdCorrect"
            onClick={() => {
              setCorrectBase(openCorrection(c));
              setDialog('correct');
            }}
          >
            Düzelt
          </button>
        ) : null}
        {mayLifecycle ? (
          /* the recipient's contact address — the correction cell, A·A·D·D (matrix D8) */
          <button
            className="btn btn-secondary"
            type="button"
            id="cdEmailFix"
            onClick={() => setDialog('email')}
          >
            E-posta adresini düzelt
          </button>
        ) : null}
        {mayLifecycle ? (
          /* `credential.revoke` — A·A·D·D */
          <button className="btn btn-dark" type="button" id="cdRevoke" onClick={() => setDialog('revoke')}>
            İptal et
          </button>
        ) : null}
      </>
    );
  else if (s === 'revoked' || s === 'superseded')
    actions = (
      <>
        {publicPageLink('Public (iptal) sayfasını aç')}
        {c.supersededByCode || !mayLifecycle ? null : (
          /* `credential.reissue` — A·A·D·D; the revoke inside it is the same transaction (rule 8) */
          <button
            className="btn btn-primary"
            type="button"
            id="cdReissue"
            onClick={() => setDialog('reissue')}
          >
            İptal et ve yeniden düzenle
          </button>
        )}
      </>
    );
  else
    actions = (
      <>
        {publicPageLink('Public sayfayı aç')}
        <button className="btn btn-secondary" type="button" id="cdCopyUrl" onClick={() => copy(publicUrl(c))}>
          Public URL kopyala
        </button>
        {canDl ? (
          <>
            <button
              className="btn btn-secondary"
              type="button"
              id="cdPng"
              onClick={() => void downloadCredentialPng(c, toast)}
            >
              PNG indir
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              id="cdPrint"
              onClick={() => void printCredential(c, toast)}
            >
              <IconPrint />
              PDF olarak Kaydet / Yazdır
            </button>
          </>
        ) : null}
        {!isOrg && perm.allowLinkedInProfile ? (
          <button className="btn btn-secondary" type="button" id="cdLi" onClick={openLinkedIn}>
            LinkedIn yardımcısını önizle
          </button>
        ) : null}
        <button className="btn btn-secondary" type="button" id="cdOg" onClick={openOg}>
          Paylaşım önizlemesi
        </button>
        {mayLifecycle ? (
          <>
            {/* `credential.correct_non_identity_metadata` — A·A*·D·D */}
            <button
              className="btn btn-secondary"
              type="button"
              id="cdCorrect"
              onClick={() => {
                setCorrectBase(openCorrection(c));
                setDialog('correct');
              }}
            >
              Yazım hatasını düzelt
            </button>
            {/* the recipient's contact address, under the same cell (matrix D8) */}
            <button
              className="btn btn-secondary"
              type="button"
              id="cdEmailFix"
              onClick={() => setDialog('email')}
            >
              E-posta düzelt
            </button>
          </>
        ) : null}
        {firstSendButton}
        {firstSendResultButton}
        {mayResend ? (
          /* `claim.resend` — A·A·A·D */
          <button
            className="btn btn-secondary"
            type="button"
            id="cdResend"
            onClick={() => {
              setResendKey(newIdempotencyKey());
              setDialog('resend');
            }}
          >
            Bilgilendirme e-postasını yeniden gönder
          </button>
        ) : null}
        {mayLifecycle ? (
          /* `credential.revoke` — A·A·D·D */
          <button className="btn btn-dark" type="button" id="cdRevoke" onClick={() => setDialog('revoke')}>
            İptal et
          </button>
        ) : null}
      </>
    );

  const issueReport = (c as { issueReport?: { at?: string; message?: string } }).issueReport;

  return (
    <section data-screen="credential">
      <div id="credDetail">
        {staleNotice}
        <div className="page-head">
          <div>
            <Link
              className="small"
              to="/admin/credentials"
              style={{ color: 'var(--text-3)', textDecoration: 'none' }}
            >
              {"← Credential'lar"}
            </Link>
            <h1 style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              {`${displayName(c)} `}
              <span className={`pill ${STATUS_PILL[s]}`}>{`${STATUS_ICON[s]} ${STATUS_TR[s]}`}</span>
            </h1>
            <p className="lead">{`${credTitle(c, 'tr')} · ${typeLabel(rel)}`}</p>
            <div className="chips" style={{ marginTop: 10 }}>
              <span className="pill pill-neutral none" title={c.releaseHash}>
                {`Release v${rel.releaseNumber} · ${String(c.releaseHash).slice(0, 10)}…`}
              </span>
              <span className="pill pill-neutral none">{`Revision r${c.revision}`}</span>
              <span className="pill pill-neutral none" title={c.renderManifestHash}>
                {`Manifest ${String(c.renderManifestHash).slice(0, 10)}…`}
              </span>
              {/* The raw status beside the word, so a test can measure the badge the reference
                  draws without reading its Turkish; it changes nothing on screen. */}
              <span className={`pill ${deliveryClass}`} data-delivery={c.deliveryStatus ?? ''}>
                {deliveryText}
              </span>
              <span className="pill pill-neutral none">{pubPolicyRec(rel.publicationPolicy).tr}</span>
              <span className={`pill ${c.publishedAt ? 'pill-active' : 'pill-info'} none`}>
                {c.publishedAt ? `Yayında · ${fmtDateTime(c.publishedAt)}` : 'Yayın bekliyor'}
              </span>
              {issueReport ? <span className="pill pill-danger none">Alıcı hata bildirdi</span> : null}
            </div>
          </div>
          <div className="page-actions">{actions}</div>
        </div>
        {issueReport ? (
          <div className="warn-box" style={{ marginBottom: 16 }}>
            <b>{`Alıcı hata bildirdi (${fmtDateTime(issueReport.at)}):`}</b>
            {` «${issueReport.message ?? ''}» — düzeltip onay e-postasını yeniden gönderin.`}
          </div>
        ) : null}
        {sup || supBy ? (
          <div className="info-box" style={{ marginBottom: 16 }}>
            {sup ? (
              <>
                {'Bu kayıt '}
                <Link to={`/admin/credentials/${sup.publicCode}`} className="mono">
                  {sup.publicCode}
                </Link>
                {' kodlu iptal edilmiş kaydın yerine düzenlendi.'}
              </>
            ) : null}
            {supBy ? (
              <>
                {'Bu kaydın yerine '}
                <Link to={`/admin/credentials/${supBy.publicCode}`} className="mono">
                  {supBy.publicCode}
                </Link>
                {` kodlu yeni kayıt düzenlendi (${displayName(supBy)}).`}
              </>
            ) : null}
          </div>
        ) : null}
        <div className="split">
          <div className="stack">
            <div className="card">
              <div className="card-head">
                <h2>Public sayfa ve bağlantılar</h2>
                <span className="small muted">
                  {pending ? 'onaya kadar kişisel veri göstermez' : 'yayında'}
                </span>
              </div>
              <div className="card-pad stack" style={{ gap: 12 }}>
                {/*
                 * TWO LINKS, TWO JOBS.
                 *
                 * They used to sit in two identical boxes, one under the other, and the only thing
                 * that told them apart was the colour of a border. One is the certificate's public
                 * verification page, which anyone may open forever; the other is one recipient's
                 * single-use acceptance link. Naming them is the whole of the change; neither
                 * address, neither permission and neither copy behaviour moved.
                 */}
                <span className="label" id="cdVerifyLabel">
                  Sertifika doğrulama bağlantısı
                </span>
                <div className="url-box">
                  <code>{publicUrl(c)}</code>
                  <button
                    className="icon-btn"
                    type="button"
                    id="cdCopyUrl2"
                    title="Kopyala"
                    aria-label="Doğrulama bağlantısını kopyala"
                    onClick={() => copy(publicUrl(c))}
                  >
                    <IconCopy />
                  </button>
                </div>
                <div className="help" id="cdVerifyHelp">
                  {'Herkese açık doğrulama sayfası; süresizdir ve onaydan önce kişisel veri göstermez.'}
                </div>

                {pending ? (
                  <span className="label" id="cdClaimLabel">
                    Alıcı onay bağlantısı
                  </span>
                ) : null}
                {pending && claimUrl.available ? (
                  <>
                    <div className="url-box" style={{ borderColor: 'var(--yellow)' }}>
                      <code>{claimUrl.url}</code>
                      <button
                        className="icon-btn"
                        type="button"
                        id="cdCopyClaim2"
                        title="Kopyala"
                        aria-label="Onay bağlantısını kopyala"
                        onClick={() => copy(claimUrl.url ?? '', 'Onay bağlantısı kopyalandı')}
                      >
                        <IconCopy />
                      </button>
                    </div>
                    <div className="help" id="cdClaimHelp">
                      {`Yalnız bu alıcıya gönderildi ve tek kullanımlıktır${
                        tok.expiresAt
                          ? `; ${fmtDate(String(tok.expiresAt).slice(0, 10))} tarihine kadar geçerlidir`
                          : ''
                      }.`}
                    </div>
                  </>
                ) : null}
                {pending && !claimUrl.available ? (
                  /*
                   * NOT a red box carrying a fake address and a dead copy button.
                   *
                   * The old cell drew «Kullanılamıyor — yeni bağlantı üretin» where a URL goes, in
                   * the shape of a URL, beside a copy control that could not copy: it read as a
                   * broken link rather than as an ordinary state of a record waiting to be
                   * accepted. What replaces it is a plain information area whose sentence comes
                   * from the SERVER's own reason, so a lapsed link, a used one, a cancelled one
                   * and one this session simply does not hold are four different sentences.
                   */
                  <div className="info-box small" id="cdClaimNotice">
                    {claimNotice(claimUrl.reason)}
                  </div>
                ) : null}

                {pending ? (
                  /*
                   * The token's own engineering detail, folded away.
                   *
                   * It used to be the paragraph under the link a person copies every day, and it
                   * read as an apology written in hashes. Nothing was deleted — what the record
                   * actually knows is still here, one click away, and what it does NOT know now
                   * says so instead of printing «NaN bit» and «NaN karakter».
                   */
                  <details id="cdTokenTech">
                    <summary className="small muted">Onay bağlantısının teknik kaydı</summary>
                    <dl className="dl small" style={{ marginTop: 8 }}>
                      <dt>Saklanan karşılık</dt>
                      <dd>
                        {knownText(tok.hash) === null ? (
                          <span className="muted">Bilgi yok</span>
                        ) : (
                          <span className="mono">{`${String(tok.hash).slice(0, 12)}…`}</span>
                        )}
                        {knownText(tok.algorithm) === null ? null : ` · ${String(tok.algorithm)}`}
                      </dd>
                      <dt>Rotasyon</dt>
                      <dd>{knownCount(tok.rotationCount) ?? '0'}</dd>
                      {knownCount(tok.entropyBits) === null ? null : (
                        <>
                          <dt>Entropi</dt>
                          <dd>{`${knownCount(tok.entropyBits)} bit`}</dd>
                        </>
                      )}
                      {knownText(tok.encoding) === null ? null : (
                        <>
                          <dt>Kodlama</dt>
                          <dd>{String(tok.encoding)}</dd>
                        </>
                      )}
                      {knownText(tok.issuedAt) === null ? null : (
                        <>
                          <dt>Üretim</dt>
                          <dd>{fmtDateTime(String(tok.issuedAt))}</dd>
                        </>
                      )}
                    </dl>
                    <p className="help">
                      {
                        "Ham token yalnız üretildiği oturumda bellekte tutulur; kalıcı snapshot'a, render'a ve public metadata'ya hiç girmez."
                      }
                    </p>
                  </details>
                ) : null}

                <dl className="dl">
                  <dt>Credential ID</dt>
                  <dd className="copy-line">
                    <span className="mono">{c.publicCode}</span>
                    <button
                      className="icon-btn"
                      type="button"
                      id="cdCopyCode"
                      title="Kopyala"
                      aria-label="Kodu kopyala"
                      onClick={() => copy(c.publicCode)}
                    >
                      <IconCopy />
                    </button>
                  </dd>
                  <dt>Görsel</dt>
                  <dd className="small muted">{imageUrl(c)}</dd>
                  <dt>Yazdırma</dt>
                  <dd className="small muted">{printUrl(c)}</dd>
                  <dt>OG görseli</dt>
                  <dd className="small muted">{`${ogUrl(c)} · 1200×630`}</dd>
                </dl>
              </div>
            </div>
            <div className="card">
              <div className="card-head">
                <h2>Kayıt</h2>
                <span className="small muted">esas kayıt; görsel yalnız sunum</span>
              </div>
              <div className="card-pad">
                <dl className="dl">
                  <dt>Subject (kimlik)</dt>
                  <dd>
                    <b>{displayName(c)}</b>{' '}
                    <span className="small muted">
                      {`· ${String((c.subjectSnapshot as { subjectType?: string }).subjectType ?? '')} · snapshot ${snapshotHash.slice(0, 10)}… · düzeltme ile devredilemez`}
                    </span>
                  </dd>
                  {editableFields<DemoFieldDefinition>(rel.fieldDefinitions)
                    .filter((f) => f.key !== rel.holderField)
                    .map((f) => (
                      <Fragment key={f.key}>
                        <dt>
                          {`${fieldLabel(f, 'tr')} `}
                          <span className="small muted">{VISIBILITY_TR[f.visibility] ?? ''}</span>
                        </dt>
                        <dd>{claimField(c, f.key) || '—'}</dd>
                      </Fragment>
                    ))}
                  <dt>İletişim e-postası</dt>
                  <dd>
                    {`${c.privateContactEmail ?? ''} `}
                    <span className="small muted">· özel; public metadata ve görsele girmez</span>
                  </dd>
                  <dt>Release</dt>
                  <dd>
                    {/* The editor is the reference's own destination for a release; the address is
                        kept as it is so the two surfaces answer the same link. */}
                    <Link to={`/admin/templates/${t ? t.id : ''}/editor`}>{t ? t.name : c.templateId}</Link>
                    {` · v${rel.releaseNumber} `}
                    <span className="small mono">{`${String(c.releaseHash).slice(0, 16)}…`}</span>
                    <br />
                    <span className="small muted">
                      Issuance anında dondurulmuş tam sözleşme; kök şablon sonradan değişse de bu kayıt
                      değişmez.
                    </span>
                  </dd>
                  <dt>Veriliş</dt>
                  <dd>{fmtDate(c.issueDate)}</dd>
                  <dt>Geçerlilik</dt>
                  <dd>
                    {s === 'expired'
                      ? `${c.expiresAt ? fmtDate(c.expiresAt) : 'Süresiz'} `
                      : c.expiresAt
                        ? fmtDate(c.expiresAt)
                        : 'Süresiz'}
                    {s === 'expired' ? <span className="pill pill-expired">doldu</span> : null}
                  </dd>
                  {c.revokedAt ? (
                    <>
                      <dt>İptal</dt>
                      <dd>
                        {`${fmtDateTime(c.revokedAt)} · `}
                        <span className="small muted">özel neden:</span>
                        {` ${String((c as { revokedReasonPrivate?: string }).revokedReasonPrivate ?? '')}`}
                      </dd>
                    </>
                  ) : null}
                  <dt>Dil</dt>
                  <dd>{trUpper(c.lang || 'tr')}</dd>
                  <dt>Render manifest</dt>
                  <dd className="small mono" style={{ wordBreak: 'break-all' }}>
                    {c.renderManifestHash}
                  </dd>
                  <dt>Oluşturan</dt>
                  <dd>
                    {`${c.createdBy} · ${fmtDateTime(c.createdAt)}${batch ? ' · ' : ' · tek kayıt'}`}
                    {/* The upload's NAME where it has one, which is what the history now lists it
                        under; the filename is still there for an upload nobody has named. */}
                    {batch ? <Link to={`/admin/import?batch=${batch.id}`}>{uploadLabel(batch)}</Link> : null}
                  </dd>
                  {/*
                   * THE GROUPS THIS RECORD IS IN (migration 028) — and the row is drawn only when
                   * there are any, so a record nobody has grouped looks exactly as it did.
                   *
                   * Read-only here on purpose: membership is edited from the register, where a set
                   * of records is what is being named. This row answers «which groups is this one
                   * in», and each name links to that group's own filtered page.
                   */}
                  {groupsOf(c).length > 0 ? (
                    <>
                      <dt>Gruplar</dt>
                      <dd id="cdGroups">
                        {groupsOf(c).map((g, at) => (
                          <span key={g.id}>
                            {at === 0 ? null : ' · '}
                            <Link to={`/admin/credentials?group=${g.id}`}>{g.name}</Link>
                          </span>
                        ))}
                      </dd>
                    </>
                  ) : null}
                  <dt>Son güncelleme</dt>
                  <dd>{fmtDateTime(c.updatedAt)}</dd>
                </dl>
              </div>
            </div>
            {/*
             * TWO CARDS, ONE PLACE — and which one is drawn is a statement about what is true here.
             *
             * With a real session there is a real backend behind this screen, so the record's history is
             * durable and is read from the server: «Kayıt ve hareket geçmişi». Without one — the visual
             * acceptance surface, which has no role and no server — the prototype’s own card stays, and
             * so does its sentence about measurement being kept for the session only, because on THAT
             * surface the sentence is true. The frozen reference is therefore untouched and needs no new
             * approved correction; what changed is the product, where the sentence was false.
             */}
            {role === null ? (
              <div className="card">
                <div className="card-head">
                  <h2>Denetim kaydı</h2>
                  <span className="small muted">append-only · kalıcı · hukuki kayıt</span>
                </div>
                <div className="tabs" id="cdEvTabs" role="tablist" style={{ padding: '0 18px' }}>
                  {/* The reference's tab strip is two anchors carrying `role="tab"`, and the pair is
                      reproduced as it is: the strip's own stylesheet rules are written for `.tabs a`,
                      and a button here would lose them. Both are in the tab order and both answer
                      Enter, which is what the role promises. */}
                  {/* biome-ignore lint/a11y/useValidAnchor: the reference's own tab strip markup */}
                  <a
                    href="#"
                    data-evtab="audit"
                    role="tab"
                    aria-selected={tab === 'audit'}
                    className={tab === 'audit' ? 'is-active' : undefined}
                    onClick={(e) => {
                      e.preventDefault();
                      setTab('audit');
                    }}
                  >
                    {`Denetim (${c.events.length})`}
                  </a>
                  {/* biome-ignore lint/a11y/useValidAnchor: the reference's own tab strip markup */}
                  <a
                    href="#"
                    data-evtab="telemetry"
                    role="tab"
                    aria-selected={tab === 'telemetry'}
                    className={tab === 'telemetry' ? 'is-active' : undefined}
                    onClick={(e) => {
                      e.preventDefault();
                      setTab('telemetry');
                    }}
                  >
                    {`Kullanım ölçümü (${usage.length})`}
                  </a>
                </div>
                <div className="card-pad">
                  <div id="cdEvAudit" hidden={tab !== 'audit'}>
                    <div className="timeline">
                      {c.events
                        .slice()
                        .reverse()
                        .map((raw) => {
                          const e = raw as DemoEvent;
                          return (
                            <div className={`ev ${e.type}`} key={e.id}>
                              <span className="dot" />
                              <div>
                                <b>{EVENT_TR[e.type] ?? e.type}</b>
                                <span>
                                  {`${fmtDateTime(e.at)} · ${e.actor}${eventMeta(e)}${e.correlationId ? ' · ' : ''}`}
                                  {e.correlationId ? (
                                    <span className="mono small">{e.correlationId}</span>
                                  ) : null}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                    <p className="help" style={{ marginTop: 10 }}>
                      {'Bu akış yalnız '}
                      <b>authoritative</b>
                      {
                        " olayları taşır: oluşturma, render, yayın, token rotasyonu, e-posta teslim denemesi, kabul, düzeltme, iptal ve yeniden düzenleme. Her kayıt actor, gerekçe (gerektiğinde), correlationId ve UTC zaman damgası taşır; append-only'dir ve kalıcı katmana yazılır."
                      }
                    </p>
                  </div>
                  <div id="cdEvTelemetry" hidden={tab !== 'telemetry'}>
                    {usage.length > 0 ? (
                      <div className="timeline">
                        {usage
                          .slice()
                          .reverse()
                          .map((e) => (
                            <div className={`ev ${e.type}`} key={e.id}>
                              <span className="dot" />
                              <div>
                                <b>{EVENT_TR[e.type] ?? e.type}</b>
                                <span>{`${fmtDateTime(e.at)} · kullanım ölçümü`}</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <p className="muted small">Bu oturumda kullanım ölçümü kaydı yok.</p>
                    )}
                    <div className="warn-box small" style={{ marginTop: 10 }}>
                      <b>Bu akış hukuki kayıt DEĞİLDİR.</b>
                      {
                        ' Sayfa görüntüleme, indirme, yazdırma ve LinkedIn yardımcısı açılışı gibi kullanıcı etkileşimleri yalnız kullanım ölçümüdür; prototipte '
                      }
                      <b>oturumluk</b>
                      {' tutulur, sayfa yenilenince silinir ve denetim kaydına karışmaz.'}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <CredentialHistoryCard code={c.publicCode} />
            )}
          </div>
          <div className="card">
            <div className="card-head">
              <h2>Render edilen görsel</h2>
              <span className="small muted">
                {`${canvasW(rel.canvas)}×${canvasH(rel.canvas)} · r${c.revision}`}
              </span>
            </div>
            {/*
             * The artwork is the record's own release and its own render data, never the mutable
             * template's — rule 4, and the reason a corrected record draws a new image.
             *
             * `data-hpw-artwork` is what makes it FIT the card. The client renderer marks its own
             * output responsive (`width:100%;height:auto`), but the stored artifact is the file the
             * manifest was hashed over: `width="1080" height="1080"` and no style, which a browser
             * lays out at 1080 real pixels and pushes off the right-hand edge. The bytes are not
             * touched — the container sizes them, keeping the viewBox's aspect ratio and cropping
             * nothing (`styles/approved-corrections.css`).
             */}
            <div
              className="card-pad"
              data-hpw-artwork=""
              style={{ background: 'var(--surface-3)' }}
              dangerouslySetInnerHTML={{ __html: credentialArtwork(c) }}
            />
            {canDl ? (
              <div className="card-foot" style={{ justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-secondary btn-sm"
                  type="button"
                  id="cdPng2"
                  onClick={() => void downloadCredentialPng(c, toast)}
                >
                  PNG indir
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  type="button"
                  id="cdPrint2"
                  onClick={() => void printCredential(c, toast)}
                >
                  <IconPrint />
                  PDF olarak Kaydet / Yazdır
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {dialog === 'revoke' ? <RevokeDialog credential={c} onClose={finishRevoke} /> : null}
      {dialog === 'resend' ? (
        <ResendDialog credential={c} available={claimUrl.available} onClose={finishResend} />
      ) : null}
      {dialog === 'firstSend' ? (
        <FirstSendPanel
          scope={{ credentialIds: [c.id] }}
          what="bu kayıt"
          credentialId={c.id}
          /* The badge on this page is the RECORD's delivery status, so the record is what gets
             read again — `useRead`'s own retry, which keeps the last answer if it fails. */
          onDeliveryMoved={lookup.retry}
          onClose={() => {
            setDialog(null);
            /* The record now has an e-mail, so the button that offered this must go — and the
               result it produced becomes reachable in its place. */
            firstSendEligible.retry();
            firstSendHistory.retry();
          }}
        />
      ) : null}
      {dialog === 'firstSendResult' && lastFirstSend !== null ? (
        <FirstSendPanel
          scope={{ credentialIds: [c.id] }}
          what="bu kayıt"
          credentialId={c.id}
          openRequestId={lastFirstSend.id}
          onDeliveryMoved={lookup.retry}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'correct' && correctBase !== null ? (
        <CorrectDialog
          credential={correctBase.credential}
          refusal={correctRefusal}
          busy={correctBusy}
          onClose={finishCorrect}
          onReissue={() => setDialog('reissue')}
        />
      ) : null}
      {dialog === 'email' ? <EmailFixDialog credential={c} onClose={finishEmailFix} /> : null}
      {dialog === 'reissue' ? (
        <ReissueDialog credential={c} templates={published} onClose={finishReissue} />
      ) : null}
      {dialog === 'linkedin' ? (
        <LinkedInDialog
          credential={c}
          fields={linkedInFields(
            c,
            linkedInOrg.source === 'unreadable' ? LI_TR.issuerUnknown : linkedInOrg.organizationName,
          )}
          issuerName={linkedInOrg.organizationName}
          pageUrl={linkedInOrg.pageUrl}
          unreadableIssuer={linkedInOrg.source === 'unreadable'}
          addToProfileUrl={addToProfileLinkForCredential(c, credTitle(c), c.release, settings).url}
          onClose={() => setDialog(null)}
          onCopy={(text, message) => copy(text, message)}
        />
      ) : null}
      {dialog === 'og' ? <OgPreviewDialog credential={c} onClose={() => setDialog(null)} /> : null}
    </section>
  );
}
